import React, { useState, useEffect } from 'react';
import {
  FileText,
  Plus,
  Trash2,
  Save,
  AlertCircle,
  Video,
  Image,
  FileCheck,
  Info,
  ChevronDown,
  ChevronUp,
  Copy,
  Sparkles,
  CheckCircle,
  Clock,
  ExternalLink,
  Shield
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import SmartEssaySyncButton from './SmartEssaySyncButton';

const EssayRequirementsForm = ({
  collegeId,
  collegeName,
  collegeWebsite = '',
  userId,
  initialRequirements = {},
  onSave,
  onCancel,
  embedded = false,
  currentUser = null
}) => {
  const [requirements, setRequirements] = useState({
    prompt_year: '2025-2026',
    verification_status: 'unverified',
    confidence_score: 0,
    last_verified_date: null,
    verified_by: null,
    source: '',
    source_url: '',
    platform: 'common_app',
    personalStatementRequired: false,
    additionalInfo: '',
    essays: [],
    previous_year: null,
    // Keep special_requirements for backwards compatibility
    special_requirements: {
      video_essay: false,
      portfolio: false,
      writing_sample: false
    }
  });

  const [expandedEssays, setExpandedEssays] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [isAIFetched, setIsAIFetched] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  
  // Helper to get verification status details
  const getVerificationStatus = () => {
    const status = requirements.verification_status;
    switch (status) {
      case 'manually_verified':
        return { color: 'emerald', icon: CheckCircle, label: 'Verified' };
      case 'ai_fetched':
        return { color: 'yellow', icon: Sparkles, label: 'AI Fetched (Needs Review)' };
      case 'official_source':
        return { color: 'azure', icon: Shield, label: 'Official Source' };
      default:
        return { color: 'red', icon: AlertCircle, label: 'Unverified' };
    }
  };
  
  const verificationStatus = getVerificationStatus();

  // Essay type options with descriptions
  const essayTypes = [
    { value: 'supplemental', label: 'Supplemental Essay', desc: 'General supplemental essay' },
    { value: 'why_us', label: '"Why Us" Essay', desc: 'Why this college specifically' },
    { value: 'why_major', label: '"Why Major" Essay', desc: 'Why this field of study' },
    { value: 'community', label: 'Community Essay', desc: 'Community involvement/impact' },
    { value: 'diversity', label: 'Diversity Essay', desc: 'Diverse perspectives/experiences' },
    { value: 'academic_interest', label: 'Academic Interest', desc: 'Academic goals and interests' },
    { value: 'extracurricular', label: 'Extracurricular Essay', desc: 'Activities outside classroom' },
    { value: 'scholarship', label: 'Scholarship Essay', desc: 'For scholarship consideration' },
    { value: 'honors_program', label: 'Honors Program', desc: 'Honors program application' },
    { value: 'short_answer', label: 'Short Answer', desc: 'Brief response questions' },
    { value: 'other', label: 'Other', desc: 'Other essay type' }
  ];

  useEffect(() => {
    if (initialRequirements && Object.keys(initialRequirements).length > 0) {
      setRequirements(prev => ({
        ...prev,
        ...initialRequirements,
        essays: initialRequirements.essays || [],
        // Ensure special_requirements exists even if not in initialRequirements
        special_requirements: {
          video_essay: false,
          portfolio: false,
          writing_sample: false,
          ...(initialRequirements.special_requirements || {})
        },
        // Set verification fields with defaults if not present
        prompt_year: initialRequirements.prompt_year || '2025-2026',
        verification_status: initialRequirements.verification_status || 'unverified',
        confidence_score: initialRequirements.confidence_score || 0
      }));
      
      // Check if this looks like AI-fetched data
      if (initialRequirements.verification_status === 'ai_fetched' ||
          (initialRequirements.essays && 
           initialRequirements.essays.length > 0 && 
           initialRequirements.essays.some(e => e.prompt && (e.wordLimit || e.word_limit)))) {
        setIsAIFetched(true);
      }
    }
  }, [initialRequirements]);

  const handleAddEssay = () => {
    const newEssay = {
      id: Date.now(),
      type: 'supplemental',
      prompt: '',
      wordLimit: 250,  // Use camelCase for consistency with new structure
      word_limit: 250,  // Keep both for backwards compatibility
      word_minimum: null,
      required: true,
      tips: '',
      notes: ''
    };
    
    setRequirements(prev => ({
      ...prev,
      essays: [...prev.essays, newEssay],
      total_essays: prev.essays.length + 1
    }));
    
    setExpandedEssays(prev => ({ ...prev, [newEssay.id]: true }));
    setIsDirty(true);
  };

  const handleRemoveEssay = (essayId) => {
    setRequirements(prev => ({
      ...prev,
      essays: prev.essays.filter(e => e.id !== essayId),
      total_essays: Math.max(0, prev.essays.length - 1)
    }));
    setIsDirty(true);
  };

  const handleEssayChange = (essayId, field, value) => {
    setRequirements(prev => ({
      ...prev,
      essays: prev.essays.map(essay => 
        essay.id === essayId ? { ...essay, [field]: value } : essay
      )
    }));
    setIsDirty(true);
  };

  const handleDuplicateEssay = (essay) => {
    const newEssay = {
      ...essay,
      id: Date.now(),
      prompt: essay.prompt + ' (copy)'
    };
    
    setRequirements(prev => ({
      ...prev,
      essays: [...prev.essays, newEssay],
      total_essays: prev.essays.length + 1
    }));
    
    setExpandedEssays(prev => ({ ...prev, [newEssay.id]: true }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    try {
      // Validate essays have prompts
      const invalidEssays = requirements.essays.filter(e => !e.prompt?.trim());
      if (invalidEssays.length > 0) {
        toast.error('All essays must have a prompt');
        return;
      }

      await onSave(requirements);
      setIsDirty(false);
      toast.success('Essay requirements saved');
    } catch (error) {
      console.error('Error saving essay requirements:', error);
      toast.error('Failed to save essay requirements');
    }
  };

  const toggleEssayExpanded = (essayId) => {
    setExpandedEssays(prev => ({
      ...prev,
      [essayId]: !prev[essayId]
    }));
  };

  const handleAIFetchComplete = (essayRequirements) => {
    console.log('[EssayRequirementsForm] AI fetch complete:', essayRequirements);

    // Update requirements with fetched data
    setRequirements(prev => ({
      ...prev,
      ...essayRequirements,
      // Ensure essays have proper structure
      essays: (essayRequirements.essays || []).map((essay, index) => ({
        id: Date.now() + index,
        prompt: essay.prompt || '',
        word_limit: essay.word_limit || essay.wordLimit || null,
        wordLimit: essay.word_limit || essay.wordLimit || null,
        word_minimum: essay.word_minimum || essay.wordMinimum || null,
        type: essay.type || 'supplemental',
        required: essay.required !== false,
        tips: essay.tips || essay.notes || ''
      }))
    }));

    setIsDirty(true);
    setIsAIFetched(true);

    toast('AI fetch complete! Review the essays and click "Save Requirements"', {
      icon: '✨',
      duration: 5000
    });
  };

  const handleSyncComplete = (results) => {
    console.log('[EssayRequirementsForm] Sync complete:', results);

    // Optionally refresh or show success message
    if (results.created > 0 || results.updated > 0) {
      toast.success('Timeline tasks updated successfully!');
    }
  };

  return (
    <div className={`${embedded ? '' : 'bg-white rounded-lg shadow-sm border border-stone-200'} p-6`}>
      <div className="mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Essay Requirements {collegeName && `for ${collegeName}`}
            </h3>
            <p className="text-sm text-slate-600">
              Define the essays required for this college application
            </p>
          </div>
        </div>
        
        {/* Verification Status Bar */}
        <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {/* Year Selector */}
            <select 
              value={requirements.prompt_year || '2025-2026'}
              onChange={(e) => {
                setRequirements(prev => ({ ...prev, prompt_year: e.target.value }));
                setIsDirty(true);
              }}
              className="px-3 py-1.5 border border-stone-300 rounded-md text-sm focus:ring-2 focus:ring-azure-500"
            >
              <option value="2025-2026">2025-2026 Application</option>
              <option value="2026-2027">2026-2027 Application</option>
              <option value="2027-2028">2027-2028 Application</option>

            </select>
            
            {/* Verification Status Badge */}
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
              verificationStatus.color === 'emerald' ? 'bg-emerald-100 text-emerald-700' :
              verificationStatus.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
              verificationStatus.color === 'azure' ? 'bg-azure-100 text-azure-700' :
              'bg-red-100 text-red-700'
            }`}>
              <verificationStatus.icon className="w-4 h-4" />
              <span>{verificationStatus.label}</span>
              {requirements.confidence_score > 0 && (
                <span className="text-xs opacity-75">({requirements.confidence_score}%)</span>
              )}
            </div>
            
            {/* Last Verified Date */}
            {requirements.last_verified_date && (
              <span className="text-sm text-stone-500">
                Last verified: {new Date(requirements.last_verified_date).toLocaleDateString()}
              </span>
            )}
          </div>
          
          {/* Verification Actions */}
          <div className="flex items-center gap-2">
            {/* Source URL */}
            {requirements.source_url && (
              <a
                href={requirements.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-azure-600 hover:text-azure-700 border border-azure-200 rounded-md hover:bg-azure-50"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                View Source
              </a>
            )}
            
            {/* Mark as Verified Button */}
            <button
              type="button"
              onClick={() => {
                setRequirements(prev => ({
                  ...prev,
                  verification_status: 'manually_verified',
                  last_verified_date: new Date().toISOString(),
                  verified_by: currentUser?.email || 'admin',
                  confidence_score: 100
                }));
                setIsDirty(true);
                toast.success('Marked as verified');
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 text-sm font-medium"
            >
              <CheckCircle className="w-4 h-4" />
              Mark as Verified
            </button>
          </div>
        </div>
        
        {/* Source URL Input */}
        <div className="mt-3">
          <input
            type="url"
            placeholder="Source URL (e.g., college admissions page)"
            value={requirements.source_url || ''}
            onChange={(e) => {
              setRequirements(prev => ({ ...prev, source_url: e.target.value }));
              setIsDirty(true);
            }}
            className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:ring-2 focus:ring-azure-500"
          />
        </div>
      </div>

      {/* AI Fetch & Sync Section */}
      {collegeId && userId && (
        <div className="mb-6 p-4 bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 rounded-lg">
          <div className="mb-3">
            <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-600" />
              AI-Powered Essay Management
            </h4>
            <p className="text-xs text-slate-600 mt-1">
              Automatically fetch essay requirements from the college's website or sync manually entered essays
            </p>
          </div>

          <SmartEssaySyncButton
            collegeId={collegeId}
            userId={userId}
            collegeName={collegeName}
            collegeWebsite={collegeWebsite || requirements.source_url}
            applicationYear={requirements.prompt_year || '2025-2026'}
            onFetchComplete={handleAIFetchComplete}
            onSyncComplete={handleSyncComplete}
            variant="compact"
          />
        </div>
      )}

      {/* Application Platforms */}
      <div className="mb-6 p-4 bg-stone-50 rounded-lg">
        <h4 className="text-sm font-medium text-slate-700 mb-3">Application Platform</h4>
        <div className="flex flex-wrap gap-4">
          <select
            value={requirements.platform || 'common_app'}
            onChange={(e) => {
              setRequirements(prev => ({
                ...prev,
                platform: e.target.value,
                personalStatementRequired: e.target.value === 'common_app' || e.target.value === 'coalition'
              }));
              setIsDirty(true);
            }}
            className="px-3 py-2 border border-stone-300 rounded-md text-sm focus:ring-2 focus:ring-azure-500"
          >
            <option value="common_app">Common Application</option>
            <option value="coalition">Coalition Application</option>
            <option value="school_specific">School's Own Application</option>
            <option value="multiple">Multiple Platforms Accepted</option>
          </select>
          
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={requirements.personalStatementRequired}
              onChange={(e) => {
                setRequirements(prev => ({
                  ...prev,
                  personalStatementRequired: e.target.checked
                }));
                setIsDirty(true);
              }}
              className="rounded border-stone-300 text-azure-600 focus:ring-azure-500"
            />
            <span className="text-sm text-slate-700">Personal Statement Required</span>
          </label>
        </div>
        
        {requirements.personalStatementRequired && (
          <div className="mt-3 p-3 bg-azure-50 rounded-md">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-azure-600 mt-0.5" />
              <p className="text-xs text-azure-700">
                Common App acceptance will automatically create a Personal Statement slot shared across all Common App schools
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Essays List */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-medium text-slate-700">
            College-Specific Essays ({requirements.essays.length})
          </h4>
          <button
            onClick={handleAddEssay}
            className="flex items-center gap-2 px-3 py-1.5 bg-azure-600 text-white rounded-md hover:bg-azure-700 text-sm"
          >
            <Plus className="w-4 h-4" />
            Add Essay
          </button>
        </div>

        {requirements.essays.length === 0 ? (
          <div className="text-center py-8 bg-stone-50 rounded-lg border-2 border-dashed border-stone-300">
            <FileText className="w-12 h-12 text-stone-400 mx-auto mb-3" />
            <p className="text-slate-600 mb-3">No essays defined yet</p>
            <button
              onClick={handleAddEssay}
              className="text-azure-600 hover:text-azure-700 text-sm font-medium"
            >
              Add your first essay requirement
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {requirements.essays.map((essay, index) => (
              <div
                key={essay.id}
                className="border border-stone-200 rounded-lg overflow-hidden"
              >
                {/* Essay Header */}
                <div className="flex items-center justify-between p-3 bg-stone-50 cursor-pointer"
                     onClick={() => toggleEssayExpanded(essay.id)}>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-slate-600">
                      #{index + 1}
                    </span>
                    <span className="px-2 py-1 bg-white rounded text-xs font-medium text-slate-700">
                      {essayTypes.find(t => t.value === essay.type)?.label || essay.type}
                    </span>
                    {essay.required ? (
                      <span className="text-xs text-red-600 font-medium">Required</span>
                    ) : (
                      <span className="text-xs text-stone-500">Optional</span>
                    )}
                    {(essay.wordLimit || essay.word_limit) && (
                      <span className="text-xs text-stone-500">
                        {essay.wordLimit || essay.word_limit} words
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicateEssay(essay);
                      }}
                      className="p-1.5 text-stone-400 hover:text-slate-600"
                      title="Duplicate essay"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveEssay(essay.id);
                      }}
                      className="p-1.5 text-red-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {expandedEssays[essay.id] ? (
                      <ChevronUp className="w-4 h-4 text-stone-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-stone-400" />
                    )}
                  </div>
                </div>

                {/* Essay Details (Expanded) */}
                {expandedEssays[essay.id] && (
                  <div className="p-4 space-y-4 bg-white">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Essay Type
                        </label>
                        <select
                          value={essay.type}
                          onChange={(e) => handleEssayChange(essay.id, 'type', e.target.value)}
                          className="w-full px-3 py-2 border border-stone-300 rounded-md focus:ring-azure-500 focus:border-azure-500"
                        >
                          {essayTypes.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Status
                        </label>
                        <select
                          value={essay.required ? 'required' : 'optional'}
                          onChange={(e) => handleEssayChange(essay.id, 'required', e.target.value === 'required')}
                          className="w-full px-3 py-2 border border-stone-300 rounded-md focus:ring-azure-500 focus:border-azure-500"
                        >
                          <option value="required">Required</option>
                          <option value="optional">Optional</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Essay Prompt
                      </label>
                      <textarea
                        value={essay.prompt}
                        onChange={(e) => handleEssayChange(essay.id, 'prompt', e.target.value)}
                        placeholder="Enter the essay prompt or question..."
                        className="w-full px-3 py-2 border border-stone-300 rounded-md focus:ring-azure-500 focus:border-azure-500"
                        rows={3}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Word Limit
                        </label>
                        <input
                          type="number"
                          value={essay.wordLimit || essay.word_limit || ''}
                          onChange={(e) => {
                            const value = e.target.value ? parseInt(e.target.value) : null;
                            // Update both fields for compatibility
                            handleEssayChange(essay.id, 'wordLimit', value);
                            handleEssayChange(essay.id, 'word_limit', value);
                          }}
                          placeholder="e.g., 250"
                          className="w-full px-3 py-2 border border-stone-300 rounded-md focus:ring-azure-500 focus:border-azure-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Word Minimum
                        </label>
                        <input
                          type="number"
                          value={essay.word_minimum || ''}
                          onChange={(e) => handleEssayChange(essay.id, 'word_minimum', e.target.value ? parseInt(e.target.value) : null)}
                          placeholder="Optional"
                          className="w-full px-3 py-2 border border-stone-300 rounded-md focus:ring-azure-500 focus:border-azure-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Tips / Notes
                      </label>
                      <textarea
                        value={essay.tips}
                        onChange={(e) => handleEssayChange(essay.id, 'tips', e.target.value)}
                        placeholder="Any tips or notes about this essay..."
                        className="w-full px-3 py-2 border border-stone-300 rounded-md focus:ring-azure-500 focus:border-azure-500"
                        rows={2}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Special Requirements */}
      <div className="mb-6 p-4 bg-stone-50 rounded-lg">
        <h4 className="text-sm font-medium text-slate-700 mb-3">Special Requirements</h4>
        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={requirements.special_requirements?.video_essay || false}
              onChange={(e) => {
                setRequirements(prev => ({
                  ...prev,
                  special_requirements: {
                    ...(prev.special_requirements || {}),
                    video_essay: e.target.checked
                  }
                }));
                setIsDirty(true);
              }}
              className="rounded border-stone-300 text-azure-600 focus:ring-azure-500"
            />
            <Video className="w-4 h-4 text-stone-500" />
            <span className="text-sm text-slate-700">Video Essay Required</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={requirements.special_requirements?.portfolio || false}
              onChange={(e) => {
                setRequirements(prev => ({
                  ...prev,
                  special_requirements: {
                    ...(prev.special_requirements || {}),
                    portfolio: e.target.checked
                  }
                }));
                setIsDirty(true);
              }}
              className="rounded border-stone-300 text-azure-600 focus:ring-azure-500"
            />
            <Image className="w-4 h-4 text-stone-500" />
            <span className="text-sm text-slate-700">Portfolio Required</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={requirements.special_requirements?.writing_sample || false}
              onChange={(e) => {
                setRequirements(prev => ({
                  ...prev,
                  special_requirements: {
                    ...(prev.special_requirements || {}),
                    writing_sample: e.target.checked
                  }
                }));
                setIsDirty(true);
              }}
              className="rounded border-stone-300 text-azure-600 focus:ring-azure-500"
            />
            <FileCheck className="w-4 h-4 text-stone-500" />
            <span className="text-sm text-slate-700">Writing Sample Required</span>
          </label>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-stone-200">
        <div className="text-sm text-stone-500">
          {isDirty && (
            <div className="flex items-center gap-1 text-amber-600">
              <AlertCircle className="w-4 h-4" />
              <span>Unsaved changes</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          {onCancel && (
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-stone-300 text-slate-700 rounded-md hover:bg-stone-50"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={!isDirty}
            className={`flex items-center gap-2 px-4 py-2 rounded-md ${
              isDirty 
                ? 'bg-azure-600 text-white hover:bg-azure-700' 
                : 'bg-stone-100 text-stone-400 cursor-not-allowed'
            }`}
          >
            <Save className="w-4 h-4" />
            Save Requirements
          </button>
        </div>
      </div>
    </div>
  );
};

export default EssayRequirementsForm;